import React from 'react';
import { Alert, Space, Tag, Tooltip, Typography, theme } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { formatDateTime } from '../../utils/datetime';

const { Text } = Typography;
const { useToken } = theme;

// Dash is the one placeholder for "no value here". It was written five
// different ways across this area, half of them with a hardcoded grey.
export const Dash: React.FC = () => <Text type="secondary">--</Text>;

export interface TargetResult {
  url: string;
  success: boolean;
  latency: number;
  error?: string;
  contentValidated?: boolean;
  contentMismatch?: boolean;
  // Never requested — disabled fleet-wide, or cut off by the cascade's budget.
  skipped?: boolean;
}

export interface StepResult {
  name: string;
  success: boolean;
  severity?: 'warn' | 'info';
  latency: number;
  error?: string;
  notes?: string[];
  targetResults?: TargetResult[];
}

export interface HistoryTarget {
  url: string;
  success: boolean;
  latency: number;
  error?: string;
  contentValidated?: boolean;
  // Never requested — disabled fleet-wide, or cut off by the cascade's budget.
  skipped?: boolean;
}

export interface HistoryStep {
  name: string;
  success: boolean;
  latency: number;
  error?: string;
  // Which URLs the step attempted and how each ended — including, on a step
  // that succeeded, the one that answered. Absent on checks recorded before
  // the history kept it, and on steps that have no targets.
  targets?: HistoryTarget[];
}

export interface HistoryEntry {
  timestamp: number;
  status: string;
  // Absent on checks recorded before the history kept more than time+state.
  latency?: number;
  totalDuration?: number;
  failedStep?: string;
  error?: string;
  steps?: HistoryStep[];
}

export interface StatusChange {
  state: string;
  // Stamped by the gateway in UTC at the moment the state was confirmed.
  at: string;
}

export const STATUS_COLORS: Record<string, string> = {
  HEALTHY: 'green', DEGRADED: 'gold', ZOMBIE: 'purple',
  ERROR: 'red', DOWN: 'red', SUSPECT: 'yellow', UNKNOWN: 'default',
  STALE: 'default',
};

// How badly a target is broken — the same ranking the gateway records the
// status timeline by (models.FailureSeverity). Zero means "not a confirmed
// failure": HEALTHY, SUSPECT, UNKNOWN. Not to be confused with the report's
// severity order, which deliberately sorts STALE below fresh outages.
export const FAILURE_SEVERITY: Record<string, number> = {
  DEGRADED: 1, ZOMBIE: 2, ERROR: 3, DOWN: 4, STALE: 5,
};

export const SPARKLINE_COLORS: Record<string, string> = {
  HEALTHY: '#52c41a', DEGRADED: '#fa8c16', SUSPECT: '#faad14',
  ZOMBIE: '#722ed1', ERROR: '#ff4d4f', DOWN: '#cf1322', UNKNOWN: '#8c8c8c',
  STALE: '#595959',
};

export const STEP_LABELS: Record<string, string> = {
  icmp_ping: 'ICMP Ping',
  globalping: 'Globalping',
  proxy_connect: 'Proxy Connect',
  proxy_request: 'Proxy Request',
  proxy_latency: 'Tunnel Latency',
  proxy_egress_geo: 'Egress Geo',
};

export const STEP_DESCRIPTIONS: Record<string, string> = {
  icmp_ping: 'ICMP ping to the IP address. 100% packet loss = potential DOWN. High loss/latency = DEGRADED.',
  globalping: 'Checks IP visibility from multiple global locations via Globalping API. Low visibility = ZOMBIE.',
  proxy_connect: 'TCP connection to proxy host:port (5s timeout). Failure = proxy is DOWN.',
  proxy_request: 'HTTP GET through the proxy to target URLs. 407 = auth ERROR, timeout = DEGRADED, content mismatch = ZOMBIE.',
  proxy_latency: 'Measures pure tunnel overhead (CONNECT/SOCKS5 handshake), excluding TLS with target. Exceeds threshold = DEGRADED.',
  proxy_egress_geo: 'Mobile only: fetches the public IP + carrier country seen through the proxy (identifies the modem).',
};

// A cascade stops at the first real failure, and the gateway records the steps
// after it as failures carrying "skipped: <why>". Drawing those as three red
// crosses hides the one that matters, so they are read back out as what they
// are: not attempted.
export const SKIPPED_PREFIX = /^skipped:\s*/i;

export function isSkippedStep(step: { success: boolean; error?: string }): boolean {
  return !step.success && SKIPPED_PREFIX.test(step.error || '');
}

/** The step that broke the cascade — the first genuine failure, never a skip. */
export function firstFailedStep<T extends { success: boolean; error?: string }>(
  steps?: T[],
): T | undefined {
  return (steps || []).find((s) => !s.success && !isSkippedStep(s));
}

export const stepLabel = (name: string) => STEP_LABELS[name] || name;

// StepResultIcon picks the step icon/colour from success + severity:
// green check / orange warning / blue info / red cross, and a grey dash for a
// step the cascade never got to.
// Hover shows the combined notes when present (also rendered in the Note column).
export const StepResultIcon: React.FC<{ step: StepResult }> = ({ step }) => {
  const { token } = useToken();
  if (isSkippedStep(step)) {
    return (
      <Tooltip title={(step.error || '').replace(SKIPPED_PREFIX, '') || 'Not attempted'}>
        <MinusCircleOutlined style={{ color: token.colorTextQuaternary }} />
      </Tooltip>
    );
  }
  if (!step.success) {
    return <CloseCircleOutlined style={{ color: token.colorError }} />;
  }
  if (step.severity === 'warn') {
    return (
      <Tooltip title={(step.notes || []).join(' · ') || 'Observation present'}>
        <ExclamationCircleOutlined style={{ color: token.colorWarning }} />
      </Tooltip>
    );
  }
  if (step.severity === 'info') {
    return (
      <Tooltip title={(step.notes || []).join(' · ') || 'Informational'}>
        <InfoCircleOutlined style={{ color: token.colorInfo }} />
      </Tooltip>
    );
  }
  return <CheckCircleOutlined style={{ color: token.colorSuccess }} />;
};

// FailureCallout is the answer to the question this whole page exists for:
// which step failed and what did it say. It is deliberately the loudest thing
// in an expanded row, and the error is never truncated — an operator cannot
// act on "dial tcp 192.0.2.10:8…".
export const FailureCallout: React.FC<{ steps?: StepResult[] }> = ({ steps }) => {
  const step = firstFailedStep(steps);
  if (!step) return null;
  const targets = step.targetResults || [];
  const failedTargets = targets.filter((t) => !t.success && !t.skipped).length;
  return (
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 8 }}
      message={
        <Text strong style={{ fontSize: 13 }}>
          Failed at {stepLabel(step.name)}
          {targets.length > 0 && ` — ${failedTargets} of ${targets.length} targets failed`}
        </Text>
      }
      description={
        <Text type="danger" style={{ fontSize: 12, wordBreak: 'break-word' }}>
          {step.error || 'The step failed without recording an error message.'}
        </Text>
      }
    />
  );
};

// FailureCell is the same answer at table density: the failing step named, its
// error underneath in full-width secondary text. Two lines, hover for the rest
// — the untruncated copy is in the FailureCallout one expand away.
export const FailureCell: React.FC<{ steps?: StepResult[]; state?: string }> = ({ steps, state }) => {
  const step = firstFailedStep(steps);
  if (!step) {
    return state === 'HEALTHY'
      ? <Text type="secondary" style={{ fontSize: 12 }}>all steps passed</Text>
      : <Dash />;
  }
  return (
    <Tooltip title={step.error || stepLabel(step.name)} placement="topLeft">
      <Space direction="vertical" size={0} style={{ lineHeight: 1.3, width: '100%' }}>
        <Text type="danger" style={{ fontSize: 12 }} ellipsis>{stepLabel(step.name)}</Text>
        {step.error && (
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>{step.error}</Text>
        )}
      </Space>
    </Tooltip>
  );
};

// Sparkline shows the shape of recent checks; onOpen makes it the way into the
// exact times behind it, which were only ever rendered as coloured bars.
// SPARKLINE_BARS is how many checks the bar strip shows. The endpoint hands
// back the whole kept history (100 entries) for the modal behind it, but the
// column is ~90px on a 1280px screen and a bar is 6px — the newest checks are
// the ones worth a glance; everything else is one click away.
export const SPARKLINE_BARS = 14;

export const Sparkline: React.FC<{ history: HistoryEntry[]; onOpen?: () => void }> = ({ history, onOpen }) => {
  if (!history || history.length === 0) return <Dash />;
  // history arrives newest-first; the strip reads left-to-right oldest-to-newest.
  const items = history.slice(0, SPARKLINE_BARS).reverse();
  const barW = 5, gap = 1, h = 18;
  const w = items.length * (barW + gap);
  const svg = (
    <svg width={w} height={h} style={{ verticalAlign: 'middle' }}>
      {items.map((entry, i) => (
        <rect key={i} x={i * (barW + gap)} y={0} width={barW} height={h}
          fill={SPARKLINE_COLORS[entry.status] || SPARKLINE_COLORS.UNKNOWN} rx={1} />
      ))}
    </svg>
  );
  if (!onOpen) return svg;
  return (
    <Tooltip
      title={history.length > items.length
        ? `${history.length} checks kept, last ${items.length} shown — click for all of them`
        : `${history.length} checks — click for times`}
    >
      <span onClick={onOpen} style={{ cursor: 'pointer' }}>{svg}</span>
    </Tooltip>
  );
};

export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const absDiff = Math.abs(diff);
  const secs = Math.floor(absDiff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// formatStaleAge returns a compact "Xd Yh" string for the failure-streak
// duration. Used to label STALE/DOWN targets so the operator can see at a
// glance which entries are settled long-term and which are fresh outages.
export function formatStaleAge(staleSince: string): string {
  const diff = Date.now() - new Date(staleSince).getTime();
  if (diff < 0) return '';
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs > 0 ? `${days}d ${remHrs}h` : `${days}d`;
}

// formatSpan renders how long one state lasted: the gap between two entries of
// the timeline, or between the last one and now.
export function formatSpan(fromISO: string, toISO?: string): string {
  const from = new Date(fromISO).getTime();
  const to = toISO ? new Date(toISO).getTime() : Date.now();
  const mins = Math.max(0, Math.floor((to - from) / 60000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return hrs % 24 > 0 ? `${days}d ${hrs % 24}h` : `${days}d`;
}

// StatusTimelineBlock is the record of how a target reached its current state:
// one line per confirmed transition, with how long that state lasted before
// the next one.
//
// The current state alone cannot distinguish a proxy that dropped dead from
// one that degraded for an hour first and was written off the next day — and
// that difference is what decides whether anyone needs to look at it. On a
// working target the list is a single HEALTHY line, which reads as "healthy
// since", the one thing worth saying about a target that is fine.
//
// The gateway stamps these in UTC; they are shown on the reader's clock like
// every other time in the UI, with the recorded UTC value one hover away.
export const StatusTimelineBlock: React.FC<{ timeline?: StatusChange[]; state?: string }> = ({ timeline, state }) => {
  if (!timeline || timeline.length === 0) return null;
  const tail = timeline[timeline.length - 1].state;
  const healthyOnly = timeline.length === 1 && tail === 'HEALTHY';
  // The list records escalations only, so the last entry is the worst the
  // target has been — which is not always what it is doing right now.
  //
  // Two traps here, both found by replaying this component over a production
  // dump. SUSPECT is severity 0: it is neither recorded nor cleared, so a
  // healthy target whose first probe fails has state=SUSPECT over a lone
  // HEALTHY entry, and comparing state to the tail by string would call that a
  // partial recovery from HEALTHY. And a failing tail cannot claim to be
  // continuous: it is stamped the FIRST time the target entered that state,
  // and a target that leaves and re-enters it is never re-stamped, so "for 14m
  // and counting" would be asserted over an outage that has run six.
  const improved = FAILURE_SEVERITY[state || ''] > 0
    && FAILURE_SEVERITY[state || ''] < FAILURE_SEVERITY[tail];
  return (
    <div style={{ marginBottom: 8 }}>
      <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
        {healthyOnly ? 'Working since' : 'How this target got here'}
      </Text>
      <Space direction="vertical" size={0}>
        {timeline.map((c, i) => {
          const next = timeline[i + 1];
          return (
            <Space key={`${c.state}-${c.at}`} size={8}>
              <Tag color={STATUS_COLORS[c.state] || 'default'} style={{ margin: 0, minWidth: 74, textAlign: 'center', fontSize: 12 }}>
                {c.state}
              </Tag>
              <Tooltip title={`Recorded ${new Date(c.at).toISOString().replace('T', ' ').slice(0, 19)} UTC`}>
                <Text style={{ fontSize: 12 }}>{formatDateTime(c.at)}</Text>
              </Tooltip>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {next
                  ? `lasted ${formatSpan(c.at, next.at)}`
                  : c.state === 'HEALTHY'
                    ? `for ${formatSpan(c.at)} and counting`
                    : `first reached ${formatSpan(c.at)} ago`}
              </Text>
            </Space>
          );
        })}
        {improved && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Now {state} — a partial recovery is not recorded; only a healthy check clears this list.
          </Text>
        )}
      </Space>
    </div>
  );
};

// renderStateWithStale keeps the state tag and its "failing for Xd Yh" hint on
// ONE line: the hint is an italic suffix, not a second row, so a table of
// broken targets is no taller than a table of healthy ones.
export function renderStateWithStale(state: string, staleSince?: string) {
  return (
    <Space size={4} style={{ flexWrap: 'nowrap' }}>
      <Tag color={STATUS_COLORS[state] || 'default'} style={{ margin: 0, fontSize: 12 }}>{state}</Tag>
      {staleSince && (
        <Tooltip title={`Failing since ${new Date(staleSince).toLocaleString()}`}>
          <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
            {formatStaleAge(staleSince)}
          </Text>
        </Tooltip>
      )}
    </Space>
  );
}
